import React, { useState, useEffect } from 'react';
import { Package, Calendar, CheckCircle, Clock, XCircle, ArrowLeft } from 'lucide-react';
import useAuthStore from '../store/authStore';
import api from '../services/api';
import { Link } from 'react-router-dom';

const MyOrders = () => {
    const user = useAuthStore(state => state.user);
    const [orders, setOrders] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchMyOrders();
    }, []);

    const fetchMyOrders = async () => {
        setLoading(true);
        try {
            const response = await api.get('/orders/client');
            setOrders(response.data);
        } catch (error) {
            console.error('Erreur lors du chargement des commandes', error);
            setOrders([]);
        } finally {
            setLoading(false);
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'pending': return <Clock className="text-yellow-500" size={20} />;
            case 'completed': return <CheckCircle className="text-green-500" size={20} />;
            case 'cancelled': return <XCircle className="text-red-500" size={20} />;
            default: return <Package className="text-gray-500" size={20} />;
        }
    };

    const getStatusText = (status: string) => {
        switch (status) {
            case 'pending': return 'En cours de traitement';
            case 'completed': return 'Livrée / Terminée';
            case 'cancelled': return 'Annulée';
            default: return status;
        }
    };

    return (
        <div className="max-w-5xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
            <Link to="/" className="inline-flex items-center gap-2 text-brand-primary hover:text-yellow-500 mb-6 font-bold transition-colors">
                <ArrowLeft size={20} /> Retour à la boutique
            </Link>
            <div className="mb-8">
                <h1 className="text-3xl font-black text-brand-dark">Mes Commandes</h1>
                <p className="text-gray-600 mt-2">Suivez l'état de vos achats et consultez votre historique.</p>
            </div>

            {loading ? (
                <div className="flex justify-center py-20">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-primary"></div>
                </div>
            ) : orders.length === 0 ? (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
                    <Package className="mx-auto h-16 w-16 text-gray-400 mb-4" />
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Aucune commande</h3>
                    <p className="text-gray-500">Vous n'avez pas encore passé de commande sur notre boutique.</p>
                </div>
            ) : (
                <div className="space-y-6">
                    {orders.map(order => (
                        <div key={order.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 hover:shadow-md transition-shadow">
                            <div className="flex items-start gap-4">
                                <div className="p-3 bg-gray-50 rounded-xl">
                                    {getStatusIcon(order.status)}
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-gray-900">{order.product_name}</h3>
                                    <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                                        <span className="flex items-center gap-1">
                                            <Calendar size={14} />
                                            {new Date(order.created_at).toLocaleDateString()}
                                        </span>
                                        <span className="font-medium text-brand-primary">
                                            {Number(order.total_amount).toLocaleString()} FCFA
                                        </span>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-full border border-gray-100">
                                <span className={`w-2 h-2 rounded-full ${
                                    order.status === 'pending' ? 'bg-yellow-500' :
                                    order.status === 'completed' ? 'bg-green-500' :
                                    'bg-red-500'
                                }`}></span>
                                <span className="text-sm font-bold text-gray-700">
                                    {getStatusText(order.status)}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default MyOrders;
